'use client'

import { useAudioPlayer } from '@/components/client/audio/use-audio-player'
import { useTranslation } from '@/hooks/client'
import SvgPause from '@/icons/solid/Pause'
import SvgPlay from '@/icons/solid/Play'
import { tw } from '@/lib/utils'

/** Resting heights of the level bars, in px inside a 12px track. */
const BAR_HEIGHTS = [4, 8, 12, 7, 9]
/** Negative delays start each bar partway through the bounce so they move out of phase. */
const BAR_DELAYS = [0, 0.3, 0.55, 0.15, 0.4]

interface ScorecardV2ListenLinkProps {
  url: string
  group?: string
  className?: string
}

/**
 * The quiet-room recording as a play icon and label, synced through the
 * audio group. The level bars appear only while
 * it plays, bouncing on the shared `soundBar` keyframes.
 */
export const ScorecardV2ListenLink = ({ url, group, className }: ScorecardV2ListenLinkProps) => {
  const { t } = useTranslation()
  const { audioRef, status, toggle, isMounted, audioProps } = useAudioPlayer({ url, group })

  return (
    <>
      <button
        type="button"
        data-tracking="ignore"
        onClick={toggle}
        disabled={!isMounted}
        aria-pressed={status.isPlaying}
        className={tw(
          'text-lapis hover:text-navy inline-flex h-7 shrink-0 cursor-pointer items-center gap-2 text-[13px] font-semibold transition-colors disabled:cursor-default disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sky',
          className
        )}
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
          {status.isPlaying ? (
            <SvgPause aria-hidden className="h-3 w-3 fill-current" />
          ) : (
            <SvgPlay aria-hidden className="h-3 w-3 fill-current" />
          )}
        </span>
        {t('product.hearThisProduct', 'Hear this product')}
        {status.isPlaying && (
          <span aria-hidden className="flex h-3 items-end gap-0.5">
            {BAR_HEIGHTS.map((height, i) => (
              <i
                key={i}
                className="block w-0.5 animate-[soundBar_0.8s_ease-in-out_infinite] rounded-full bg-current motion-reduce:animate-none"
                style={{ height, animationDelay: `-${BAR_DELAYS[i]}s` }}
              />
            ))}
          </span>
        )}
      </button>
      {isMounted && <audio ref={audioRef} {...audioProps} />}
    </>
  )
}
